<template>
    <v-container class="py-12">
        <!-- HEADER -->
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">{{ page.title }}</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems" />
                    </div>

                    <div class="button-group">
                        <v-btn color="primary" elevation="0" dark :loading="loading"
                            @click="page.isNew ? add() : update()">
                            Salvar
                        </v-btn>

                        <v-btn color="grey lighten-1" elevation="0" dark @click="goBack">
                            Voltar
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- FORM -->
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-text>
                        <v-form ref="form" v-model="valid" lazy-validation>
                            <!-- DADOS -->
                            <h3 class="mb-4 text-h5 font-weight-bold black--text">
                                Detalhes
                            </h3>

                            <v-row>
                                <v-col>
                                    <v-text-field v-model="dataset.data.name" label="Nome" outlined dense
                                        :rules="[$rules.required]" />
                                </v-col>

                                <v-col>
                                    <v-select v-model="dataset.data.user_type" :items="dataset.userTypes"
                                        item-text="name" item-value="id" label="Tipo de usuário" outlined dense
                                        :rules="[$rules.required]" />
                                </v-col>
                            </v-row>

                            <v-divider class="my-6" />

                            <!-- PERMISSÕES -->
                            <h3 class="mb-4 text-h5 font-weight-bold black--text">
                                Permissões
                            </h3>

                            <v-row v-if="permissions.length === 0">
                                <v-col>
                                    <span>Nenhuma permissão encontrada</span>
                                </v-col>
                            </v-row>

                            <v-row v-else>
                                <v-col v-for="module in permissions" :key="module.key" cols="12" class="mb-6">
                                    <h4 class="mb-3 font-weight-bold">
                                        {{ module.title }}
                                    </h4>

                                    <v-row>
                                        <v-col v-for="permission in module.permissions" :key="permission.id" cols="12"
                                            md="4">
                                            <v-checkbox v-model="selectedPermissions" :value="permission.id"
                                                :label="permission.description" color="primary" hide-details />
                                        </v-col>
                                    </v-row>

                                    <v-divider class="mt-4" />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from "axios";
import userTypes from "@/common/data/user-types.data.js";

export default {
    name: "RolesDetails",

    data: () => ({
        loading: false,
        valid: true,

        page: {
            title: "",
            isNew: false
        },

        dataset: {
            data: {
                name: "",
                user_type: null
            },
            userTypes
        },

        permissions: [],          // módulos + permissões
        selectedPermissions: []   // IDs das permissões selecionadas
    }),

    computed: {
        breadcrumbItems() {
            return [
                { text: "Home", to: "/home", exact: true },
                { text: "Papéis", to: "/papeis", exact: true },
                {
                    text: this.page.isNew ? "Novo" : "Editar",
                    disabled: true
                }
            ];
        }
    },

    created() {
        this.setPage();
        this.load();
        this.loadPermissions();
    },

    methods: {
        setPage() {
            this.page.isNew = this.$route.params.id === "novo";
            this.page.title = this.page.isNew
                ? "Novo papel"
                : "Detalhes do papel";
        },

        async loadPermissions() {
            try {
                const response = await axios.get("/api/v2/roles/permissions");
                this.permissions = response.data.content.data;
            } catch (error) {
                console.error(error);
                this.$dialog.notify.error(
                    "Erro ao carregar permissões",
                    { position: "top-right", timeout: 4000 }
                );
            }
        },

        async load() {
            if (this.page.isNew) return;

            this.loading = true;
            try {
                const response = await axios.get(
                    `/api/v2/roles/${this.$route.params.id}`
                );

                const role = response.data.content;

                this.dataset.data.name = role.name;
                this.dataset.data.user_type = role.user_type;
                this.selectedPermissions = role.permissions.map((x) => x.permission_id) || [];
            } catch (error) {
                console.error(error);
                this.$dialog.notify.error(
                    "Erro ao carregar papel",
                    { position: "top-right", timeout: 4000 }
                );
                this.goBack();
            } finally {
                this.loading = false;
            }
        },

        buildPayload() {
            return {
                name: this.dataset.data.name,
                user_type: this.dataset.data.user_type,
                permissions: this.selectedPermissions
            };
        },

        async add() {
            if (!this.$refs.form.validate()) return;

            const confirm = await this.$dialog.confirm({
                title: "Papel",
                text: "Deseja salvar o papel?",
                actions: { false: "Cancelar", true: "Salvar" }
            });

            if (!confirm) return;

            try {
                await axios.post("/api/v2/roles", this.buildPayload());
                this.$dialog.notify.info("Papel criado com sucesso");
                this.goBack();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error));
            }
        },

        async update() {
            if (!this.$refs.form.validate()) return;

            const confirm = await this.$dialog.confirm({
                title: "Papel",
                text: "Deseja atualizar o papel?",
                actions: { false: "Cancelar", true: "Atualizar" }
            });

            if (!confirm) return;

            try {
                await axios.patch(
                    `/api/v2/roles/${this.$route.params.id}`,
                    this.buildPayload()
                );
                this.$dialog.notify.info("Papel atualizado com sucesso");
                this.goBack();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error));
            }
        },

        goBack() {
            this.$router.push("/papeis");
        }
    }
};
</script>